/* import __COLOCATED_TEMPLATE__ from './two-fa-authentication-config.hbs'; */
/* RESPONSIBLE TEAM: team-app-security */

import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type IntlService from 'ember-intl/services/intl';
import ajax from 'embercom/lib/ajax';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type Admin from 'embercom/models/admin';

interface Args {}

interface Signature {
  Args: Args;
  Element: never;
}

type OtpAction = 'disable-2fa' | 'recovery-codes';

export default class TwoFaAuthenticationConfig extends Component<Signature> {
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare intercomConfirmService: $TSFixMe;
  @service declare identityVerificationRiskStateService: $TSFixMe;
  @service declare router: $TSFixMe;

  @tracked isTwoFaConfigDrawerOpen = false;
  @tracked validationCode = '';
  @tracked isOtpModalOpen = false;
  @tracked otpAction: OtpAction | null = null;
  @tracked isOtpLoading = false;
  @tracked otpErrorMessage = '';
  @tracked isRecoveryCodesModalOpen = false;
  @tracked recoveryCodes = '';

  constructor(owner: $TSFixMe, args: Args) {
    super(owner, args);
    this.handleUrlHash();
  }

  @action
  handleUrlHash() {
    if (window.location.hash === '#generate-otp-codes') {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      this.requestRecoveryCodes();
    }
  }

  get admin() {
    return this.appService.app.currentAdmin as Admin;
  }

  get isTwoFaEnabled() {
    return this.admin.has_enabled_2fa;
  }

  get hasSixDigitValidationCode() {
    return this.validationCode?.length === 6;
  }

  get isAuthEnableTaskRunning() {
    return taskFor(this.enable2FAuth).isRunning;
  }

  get isSideDrawerSaveBtnDisabled() {
    return !this.hasSixDigitValidationCode || this.isAuthEnableTaskRunning;
  }

  get otpModalTitle() {
    if (this.otpAction === 'disable-2fa') {
      return this.intl.t('apps.app.account.two-factor-auth.disable-2fa-title');
    } else if (this.otpAction === 'recovery-codes') {
      return this.intl.t('account.general.download-recovery-codes');
    }
    return this.intl.t('apps.app.account.two-factor-auth.enter-code');
  }

  get otpModalDescription() {
    if (this.otpAction === 'disable-2fa') {
      return this.intl.t('apps.app.account.two-factor-auth.disable-2fa-description');
    } else if (this.otpAction === 'recovery-codes') {
      return this.intl.t('apps.app.account.two-factor-auth.enter-code-from-authenticator');
    }
    return this.intl.t('apps.app.account.two-factor-auth.enter-code-from-authenticator');
  }

  get otpModalConfirmButtonText() {
    if (this.otpAction === 'disable-2fa') {
      return this.intl.t('apps.app.account.two-factor-auth.disable-2fa-confirm');
    } else if (this.otpAction === 'recovery-codes') {
      return this.intl.t('account.general.download-recovery-codes');
    }
    return this.intl.t('apps.app.account.two-factor-auth.validate-button');
  }

  @action
  learnDropdownSelectItem(item: { label: string; text: string }) {
    if (item.label === 'google-auth') {
      safeWindowOpen('https://support.google.com/accounts/answer/1066447?hl=en');
    }
  }

  private toggleConfigDrawer(isOpen: boolean) {
    this.isTwoFaConfigDrawerOpen = isOpen;
    if (isOpen) {
      this.admin.getTwoFactorQRCodeSVG();
    }
  }

  @action
  toggle2FAConfiguration() {
    if (this.admin.has_enabled_2fa) {
      this.showOtpModal('disable-2fa');
    } else {
      this.toggleConfigDrawer(true);
    }
  }

  @action
  toggle2FAConfigDrawer(isOpen: boolean) {
    this.toggleConfigDrawer(isOpen);
  }

  @action
  showOtpModal(action: OtpAction) {
    this.otpAction = action;
    this.isOtpModalOpen = true;
    this.otpErrorMessage = '';
  }

  @action
  closeOtpModal() {
    this.isOtpModalOpen = false;
    this.otpAction = null;
    this.isOtpLoading = false;
    this.otpErrorMessage = '';
  }

  @action
  closeRecoveryCodesModal() {
    this.isRecoveryCodesModalOpen = false;
    this.recoveryCodes = '';
  }

  @action
  clearOtpError() {
    this.otpErrorMessage = '';
  }

  @action
  requestRecoveryCodes() {
    this.showOtpModal('recovery-codes');
  }

  @action
  onOtpConfirm(otpCode: string) {
    if (this.otpAction === 'disable-2fa') {
      taskFor(this.disable2FAWithOtp).perform(otpCode);
    } else if (this.otpAction === 'recovery-codes') {
      taskFor(this.requestRecoveryCodesWithOtp).perform(otpCode);
    }
  }

  @dropTask
  *enable2FAuth() {
    if (this.hasSixDigitValidationCode) {
      try {
        yield ajax({
          type: 'PUT',
          url: '/ember/admins/enable_two_factor_auth',
          data: JSON.stringify({
            id: this.admin.id,
            app_id: this.admin.current_app_id,
            validation_code: this.validationCode,
          }),
        });
        this.notificationsService.notifyConfirmation(
          this.intl.t('apps.app.account.two-factor-auth.verification-enabled'),
        );
        this.admin.set('has_enabled_2fa', true);
        this.validationCode = '';
        this.toggleConfigDrawer(false);
        this.identityVerificationRiskStateService.start(this.appService.app);
      } catch (err) {
        this._handleErrorResponse(err);
        this.validationCode = '';
      }
    } else if (this.validationCode && !this.hasSixDigitValidationCode) {
      this.notificationsService.notifyWarning(
        this.intl.t('apps.app.account.two-factor-auth.six-digit-length'),
      );
    } else {
      this.notificationsService.notifyWarning(
        this.intl.t('apps.app.account.two-factor-auth.code-empty'),
      );
    }
  }

  @dropTask
  *disable2FAWithOtp(verificationCode: string) {
    this.isOtpLoading = true;
    this.otpErrorMessage = '';

    try {
      yield ajax({
        type: 'PUT',
        url: '/ember/admins/disable_two_factor_auth',
        data: JSON.stringify({
          id: this.admin.id,
          app_id: this.admin.current_app_id,
          validation_code: verificationCode,
        }),
      });
      this.notificationsService.notifyConfirmation(this.intl.t('account.general.2fa-was-disabled'));
      this.admin.set('has_enabled_2fa', false);
      this.identityVerificationRiskStateService.start(this.appService.app);
      this.closeOtpModal();
    } catch (err) {
      this.isOtpLoading = false;
      if (err.jqXHR.status === 403) {
        this.otpErrorMessage = this.intl.t('apps.app.account.two-factor-auth.invalid-otp-code');
      } else {
        this.otpErrorMessage = this.intl.t('account.general.cannot-update-2fa-try-again');
      }
    }
  }

  @dropTask
  *requestRecoveryCodesWithOtp(verificationCode: string): Generator<any, void, any> {
    this.isOtpLoading = true;
    this.otpErrorMessage = '';

    try {
      let response = yield ajax({
        type: 'POST',
        url: '/admins/send_recovery_codes',
        data: JSON.stringify({
          validation_code: verificationCode,
          app_id: this.admin.current_app_id,
        }),
        dataType: 'text',
      });

      if (response) {
        this.recoveryCodes = response;
        this.closeOtpModal();
        this.isRecoveryCodesModalOpen = true;
      }
    } catch (err) {
      this.isOtpLoading = false;
      if (err.jqXHR.status === 403) {
        this.otpErrorMessage = this.intl.t('apps.app.account.two-factor-auth.invalid-otp-code');
      } else {
        this.otpErrorMessage = this.intl.t(
          'account.general.cannot-download-recovery-codes-try-again',
        );
      }
    }
  }

  _handleErrorResponse(error: $TSFixMe) {
    if (error.jqXHR.status === 403) {
      this.notificationsService.notifyError(
        this.intl.t('apps.app.account.two-factor-auth.could-not-validate'),
      );
    } else {
      this.notificationsService.notifyError(
        this.intl.t('apps.app.account.two-factor-auth.try-again'),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Personal::AccountSecurity::TwoFaAuthenticationConfig': typeof TwoFaAuthenticationConfig;
  }
}
